import {
	Dropdown,
	companyNatureOptions,
	locationPreferenceOptions,
	natureOfBusinessOptions,
	ownershipOptions,
} from 'shared-utils';
import React, { useState } from 'react';
import { Button, Label, TextInput } from 'flowbite-react';
import Select, { SingleValue } from 'react-select';
import { CompanyCreateInfo, CompanyNature, CompanyOwnershipType, CompanyType, NatureOfBusinessType, UserRole } from 'types';
import { Loader } from 'ui';
import { useMutation, useQueryClient } from 'react-query';
import { editCompany } from 'src/apis/company';
import moment from 'moment';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetCompanyByIdQuery } from 'src/hooks/company.hooks';
import { useUserContext } from 'src/context/UserProvider';

function AMEditCompany() {
	const navigate = useNavigate();
	const { companyId } = useParams();
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const [searchParams] = useSearchParams();

	const [companyDetail, setCompanyDetail] = useState<CompanyCreateInfo>({
		companyType: CompanyType.LARGE_ENTERPRISE,
		name: '',
		url: '',
	});

	useGetCompanyByIdQuery(companyId ?? '', data => {
		setCompanyDetail({
			companyType: data.company?.companyType as CompanyType,
			name: data.company?.name || '',
			url: data.company?.url || '',
			bdpoc: data.company?.bdpoc || '',
			brandScore: data.company?.brandScore,
			foundedIn: data.company?.foundedIn,
			headCount: data.company?.headCount,
			headQuarter: data.company?.headQuarter,
			industry: data.company?.industry,
			isLead: data.company?.isLead,
			logo: data.company?.logo,
			nature: data.company?.nature,
			natureOfBusiness: data.company?.natureOfBusiness,
			ownership: data.company?.ownership,
			stageScore: data.company?.stageScore,
			rating: data.company?.rating,
			prePlacementCallRecording: data.company?.prePlacementCallRecording || '',
		});
	});

	const editCompanyFn = useMutation(['editCompany'], editCompany, {
		onSuccess() {
			queryClient.refetchQueries(['getMyCompaniesQuery']);
			console.log('searchParams.get:', searchParams.get('navigateTo'))
			if (searchParams.get('navigateTo') === 'goBack') {
				navigate(-1);
				navigate(-1);
			} else {
				navigate('/');
			}
		},
	});

	const handleChange = (change: Partial<CompanyCreateInfo>) => {
		setCompanyDetail({
			...companyDetail,
			...change,
		});
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		editCompanyFn.mutate({ id: companyId || '', payload: companyDetail });
	};

	const allLocationOptions = locationPreferenceOptions.map(l => ({ label: l, value: l }));
	const labelStyle = `dark:text-white text-[#111928]`;
	return (
		<div className="md:py-6">
			<div className="max-w-[580px] mx-auto dark:bg-[#111E2C] bg-[#fff] p-4 md:p-6 md:rounded-lg border dark:border-0">
				<h1 className="text-xl md:text-2xl font-bold dark:text-white text-[#28293D] mb-2">Edit Company</h1>
				<form className="flex font-inter flex-col gap-4 mt-[20px]" onSubmit={handleSubmit}>
					{/* Company Name */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="name" value="Company Name" className={`${labelStyle}`} />
							{user?.role === UserRole.SUPER_ACCOUNT_MANAGER && <span className="text-red-600 pl-1">*</span>}
						</div>
						<TextInput
							type="text"
							value={companyDetail.name}
							onChange={e => handleChange({ name: e.target.value })}
							disabled={user?.role === UserRole.ACCOUNT_MANAGER}
							placeholder="LevelUp"
						/>
					</div>

					{/* Company URL */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Company URL" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="text"
							value={companyDetail.url}
							onChange={e => handleChange({ url: e.target.value })}
							placeholder="https://www.levelup.masaischool.com"
						/>
					</div>

					{/* Location */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headQuarter" value="Location" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={allLocationOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							value={allLocationOptions.find(option => option.value === companyDetail.headQuarter)}
							onChange={(val: SingleValue<Dropdown>) => handleChange({ headQuarter: val?.value })}
							placeholder="Bengaluru, Karnataka"
						/>
					</div>

					{/* Industry Type */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="nature" value="Industry Type" className={`${labelStyle}`} />
						</div>
						<Select
							options={companyNatureOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							value={companyNatureOptions.find(option => option.value === companyDetail.nature)}
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ nature: val?.value, industry: val?.value === CompanyNature.PRODUCT ? '1' : '2' })
							}
							placeholder="Product"
						/>
					</div>

					{/* Founded In */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Founded In" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="number"
							step={1}
							min={1800}
							max={moment().year()}
							value={companyDetail.foundedIn}
							onChange={e => handleChange({ foundedIn: Number(e.target.value) })}
							placeholder="2023"
						/>
					</div>

					{/* Head Count */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headCount" value="Head Count" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="number"
							step={1}
							min={0}
							value={companyDetail.headCount}
							onChange={e => handleChange({ headCount: Number(e.target.value) })}
							placeholder="100"
						/>
					</div>

					{/* Ownership */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="ownership" value="Ownership" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={ownershipOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							value={ownershipOptions.find(option => option.value === companyDetail.ownership)}
							onChange={(val: SingleValue<Dropdown>) => handleChange({ ownership: val?.value as CompanyOwnershipType })}
							placeholder="Product"
						/>
					</div>

					{/* Nature of Business */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="natureOfBusiness" value="Nature of Business" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={natureOfBusinessOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							value={natureOfBusinessOptions.find(option => option.value === companyDetail.natureOfBusiness)}
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ natureOfBusiness: val?.value as NatureOfBusinessType })
							}
							placeholder="Product"
						/>
					</div>

					{/* Pre-Placement Call Recording Link */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="prePlacementCallRecording" value="Pre-Placement Call Recording Link" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="url"
							onChange={e => handleChange({ prePlacementCallRecording: e.target.value })}
							placeholder="https://..."
							value={companyDetail.prePlacementCallRecording || ''}
						/>
					</div>

					<div className="bg-white dark:bg-[#111E2C] sticky bottom-0 mb-[-16px] md:mb-[-24px] mx-[-16px] md:mx-[-24px] shadow-up px-[16px] md:px-[24px] rounded-b-md">
						<Button
							type="submit"
							color="purplePlaced"
							className="my-3 w-full"
							disabled={
								user?.role == UserRole.SUPER_ACCOUNT_MANAGER && !companyDetail.name ||
								!companyDetail.url ||
								!companyDetail.headQuarter ||
								!companyDetail.foundedIn ||
								!companyDetail.ownership ||
								!companyDetail.natureOfBusiness
							}
						>
							{editCompanyFn.isLoading ? <Loader /> : 'Submit'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default AMEditCompany;
